import { useState, useEffect } from "react";
import { getDownloadURL, listAll, ref, uploadBytesResumable, getMetadata } from "firebase/storage";
import { storage, db, auth } from "../firebase";
import { doc, onSnapshot, setDoc, getDoc, collection, getDocs } from "firebase/firestore";
import Sidebar from "../components/Sidebar";
import { UploadCloud } from "lucide-react";
import Header from "../components/Header";
import { Eye, Trash2 } from "lucide-react";
import { deleteObject } from "firebase/storage";
import { deleteDoc } from "firebase/firestore";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import imageCompression from 'browser-image-compression';

function MyAssets() {
  const [image, setImage] = useState(null);
  const [imageName, setImageName] = useState("");
  const [category, setCategory] = useState("");
  const [type, setType] = useState("");
  const [subType, setSubType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [imageUrls, setImageUrls] = useState([]);
  const [credits, setCredits] = useState(0);
  const [hoveredImage, setHoveredImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const imagesPerPage = 12;
  const [loadingImages, setLoadingImages] = useState(true);
  const [filterCategory, setFilterCategory] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterSubType, setFilterSubType] = useState("");
  const [modalImage, setModalImage] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const filteredImages = imageUrls.filter((item) => {
    return (
      (filterCategory === "" || item.category === filterCategory) &&
      (filterType === "" || item.type === filterType) &&
      (filterSubType === "" || item.subType === filterSubType)
    );
  });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchImages();
        fetchCredits();
      }
    });

    return () => unsubscribe();
  }, []);

  const handlePreview = (imageUrl) => {
    setModalImage(imageUrl);
  };
  

  const fetchCredits = async () => {
    const user = auth.currentUser;
    if (!user) return;
  
    const userRef = doc(db, "users", user.uid);
    const docSnap = await getDoc(userRef);
  
    if (docSnap.exists()) {
      setCredits(docSnap.data().credits || 0);
    } else {
      await setDoc(userRef, { credits: 0 }, { merge: true });
      setCredits(0);
    }
  };
  


const handleFileChange = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const validTypes = ["image/png", "image/jpeg", "image/jpg"];
  const maxSize = 10 * 1024 * 1024; // 10MB

  if (!validTypes.includes(file.type)) {
    toast.error("Only JPG, JPEG, and PNG files are allowed.");
    return;
  }

  const imageUrl = URL.createObjectURL(file);
  const img = new Image();

  img.onload = async () => {
    const { width, height } = img;

    if (width < 500 || height < 500) {
      toast.error("Image resolution must be at least 500x500 pixels.");
      return;
    }

    if (width > 3000 || height > 3000) {
      // Resize to fit within 3000x3000 while maintaining aspect ratio
      const scaleFactor = Math.min(3000 / width, 3000 / height);
      const newWidth = Math.round(width * scaleFactor);
      const newHeight = Math.round(height * scaleFactor);

      const canvas = document.createElement("canvas");
      canvas.width = newWidth;
      canvas.height = newHeight;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      canvas.toBlob((blob) => {
        if (!blob) {
          toast.error("Failed to process resized image.");
          return;
        }

        const resizedFile = new File([blob], file.name, {
          type: file.type,
          lastModified: Date.now(),
        });

        if (resizedFile.size > maxSize) {
          toast.error("Resized image exceeds 10MB. Please try another.");
          return;
        }

        setImage(resizedFile);
        setImageName(file.name);
        setImagePreview(URL.createObjectURL(resizedFile));
      }, file.type === "image/png" ? "image/png" : "image/jpeg", 1.0);
    } else {
      setImage(file);
      setImageName(file.name);
      setImagePreview(imageUrl);
    }
  };

  img.onerror = () => {
    toast.error("Invalid image file.");
  };

  img.src = imageUrl;
};


const handleUpload = async () => {
  const user = auth.currentUser;
  if (!user) {
    toast.error("User not authenticated. Please log in again.");
    return;
  }

  if (!image || !imageName || !category) {
    toast.error("Please select an image, provide image name, and select category.");
    return;
  }

  // For Garment category, also require type and subType
  if (category === "Garment" && (!type || !subType)) {
    toast.error("Please provide type and sub-type for garment images.");
    return;
  }

  // ❌ Removed credits check so free plan users can still upload
  // if (credits <= 0) {
  //   toast.error("You don't have enough credits to upload an image.");
  //   return;
  // }

  const categoryFolder = category === "Garment" ? "garments" : "models";
  const userAssetsRef = doc(db, "users", user.uid, "assets", imageName);

  const existingImageSnap = await getDoc(userAssetsRef);
  if (existingImageSnap.exists()) {
    toast.error(`An image with the name "${imageName}" already exists. Please choose a different name.`);
    return;
  }

  setUploading(true);

  const fileExtension = image.name.split('.').pop();
  const newFileName = `${imageName}.${fileExtension}`;
  const userFolder = `assets/${user.uid}/${categoryFolder}/${newFileName}`;

  const storageRef = ref(storage, userFolder);
  const uploadTask = uploadBytesResumable(storageRef, image);

  uploadTask.on(
    "state_changed",
    (snapshot) => {
      const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
      setUploadProgress(progress);
    },
    (error) => {
      console.error("Upload failed:", error);
      toast.error(`Upload failed: ${error.message}`);
      setUploading(false);
    },
    async () => {
      try {
        const downloadURL = await getDownloadURL(storageRef);

        await setDoc(userAssetsRef, {
          url: downloadURL,
          imageName,
          category,
          type,
          subType,
          storagePath: userFolder,
          timeCreated: Date.now()
        });

        fetchImages();
        setImage(null);
        setImagePreview(null);
        setImageName("");
        setType("");
        setSubType("");
        toast.success("✅ Image uploaded successfully!");
      } catch (error) {
        console.error("Error saving to Firestore:", error);
        toast.error("Error saving to Firestore.");
      } finally {
        setUploading(false);
      }
    }
  );
};
const fetchImages = async () => {
  const user = auth.currentUser;
  if (!user) return;
  setLoadingImages(true);

  const userGarmentsFolder = `assets/${user.uid}/garments/`;
  const userModelsFolder = `assets/${user.uid}/models/`;

  try {
    // List garment & model images in parallel
    const [garments, models] = await Promise.all([
      listAll(ref(storage, userGarmentsFolder)),
      listAll(ref(storage, userModelsFolder))
    ]);

    // Fetch all Firestore asset docs once (no per-item calls)
    const assetDocsSnap = await getDocs(collection(db, "users", user.uid, "assets"));
    const assetDocs = {};
    assetDocsSnap.forEach(docSnap => {
      assetDocs[docSnap.id] = docSnap.data();
    });

    // Helper to process images like StyleUAI (fast)
    const processItems = async (items, category, basePath) => {
      const results = await Promise.all(items.map(async (item) => {
        const url = await getDownloadURL(item);

        // Extract name without extension
        const imageNameWithoutExt = item.name.split(".")[0];

        // Extract timestamp from filename (if using _<timestamp>_ pattern)
        const match = item.name.match(/_(\d{13})_/);
        const stamp = match ? Number(match[1]) : 0;

        // Match Firestore metadata if exists
        const firestoreData = assetDocs[imageNameWithoutExt] || {};

        // Use Firestore timestamp if available, otherwise fallback to filename timestamp
        let timeCreated = 0;
        if (firestoreData.timestamp) {
          timeCreated = firestoreData.timestamp;
        } else if (firestoreData.timeCreated) {
          timeCreated = firestoreData.timeCreated;
        } else if (stamp) {
          timeCreated = stamp;
        }

        return {
          url,
          category,
          imageName: imageNameWithoutExt,
          type: firestoreData.type || "",
          subType: firestoreData.subType || "",
          storagePath: `${basePath}${item.name}`,
          timeCreated: timeCreated
        };
      }));

      return results;
    };

    // Process garments & models
    const garmentData = await processItems(garments.items, "Garment", userGarmentsFolder);
    const modelData = await processItems(models.items, "Model", userModelsFolder);

    // Merge & sort newest first
    const allImages = [...garmentData, ...modelData].sort((a, b) => b.timeCreated - a.timeCreated);

    setImageUrls(allImages);
  } catch (error) {
    console.error("Error fetching images:", error);
    toast.error("Error loading images.");
  }

  setLoadingImages(false);
};


  const handleDelete = async (storagePath) => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      // Delete from Firebase Storage
      const storageRef = ref(storage, storagePath);
      await deleteObject(storageRef);

      // Extract Image Name Without Extension
      const fileNameWithExtension = storagePath.split("/").pop();
      const imageName = fileNameWithExtension.split(".")[0];

      // Delete from Firestore Database
      const imageDocRef = doc(db, "users", user.uid, "assets", imageName);
      await deleteDoc(imageDocRef);

      // Update State
      setImageUrls((prev) => prev.filter((img) => img.storagePath !== storagePath));

      // Show success message
      toast.success("Image deleted successfully!");
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Failed to delete image. Please try again.");
    }
  };

  // Calculate pagination
  const indexOfLastImage = currentPage * imagesPerPage;
  const indexOfFirstImage = indexOfLastImage - imagesPerPage;
  const currentImages = filteredImages.slice(indexOfFirstImage, indexOfLastImage);
  const totalPages = Math.ceil(filteredImages.length / imagesPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="flex min-h-screen">
    <div className="hidden md:block">
    <Sidebar />
  </div>

   <main className="flex-1 p-3 md:p-10 bg-white mt-8 md:mt-0">
  <div className="absolute top-2 md:top-4 right-2 md:right-4">
    <Header />
  </div>

  <div className="block md:hidden">
    <Sidebar />
  </div>
<div className="pricing-main">
  <h2 className="mt-0 md:mt-8 text-2xl md:text-3xl font-semibold">
    Add New Assets
  </h2>

  <p className="text-gray-500 text-sm md:text-base">Upload models and garment images.</p>
  <hr className="my-2 md:my-3 border-gray-300" />

  <div className="mt-1 md:mt-2 text-lg font-semibold text-gray-700">
    Available Credits: <span className="text-purple-600">{credits}</span>
  </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
        <label className="flex flex-col items-center justify-center bg-[#F5F2FF] border-dashed border-2 border-white p-8 md:p-10 rounded-lg text-center cursor-pointer hover:border-purple-600 relative w-full max-w-md mx-auto">
        {imagePreview ? (
          <img
            src={imagePreview}
            alt="Preview"
            className="w-full h-48 object-contain rounded-md"
          />
        ) : (
          <>
            <UploadCloud className="mx-auto text-[#3913C5]" size={40} />
            <p className="text-gray-700 font-medium text-sm md:text-base mt-2">
              Select Image to upload
            </p>
            <p className="text-xs text-gray-500">Supported format: PNG/JPG/JPEG (Max: 10MB)</p>
          </>
        )}

        <input
          type="file"
          className="hidden"
          onChange={handleFileChange}
        />

        <button 
          type="button" 
          className="mt-4 px-4 py-2 bg-white text-[#3913C5] text-sm font-medium rounded-lg flex items-center gap-2"
          onClick={(e) => {
            e.stopPropagation();
            e.target.previousSibling.click();
          }}
        >
          {imagePreview ? "Change Image" : "Select File"} <UploadCloud size={16} />
        </button>
      </label>


          

          <div className="flex flex-col gap-3">
            <input type="text" placeholder="Image name" value={imageName} onChange={(e) => setImageName(e.target.value)}
              className="border border-gray-300 bg-white p-2 rounded-md text-sm md:text-base" />

            <select value={category} onChange={(e) => {
              setCategory(e.target.value);
              // Clear type and subType when switching to Model
              if (e.target.value === "Model") {
                setType("");
                setSubType("");
              }
            }}
              className="border border-gray-300 p-2 rounded-md text-sm md:text-base bg-white">
              <option value="">Select Category</option> 
              <option value="Model">Model</option>
              <option value="Garment">Garment</option>
            </select>

            {category === "Garment" && (
              <>
                <select value={type} onChange={(e) => setType(e.target.value)}
                  className="border border-gray-300 p-2 rounded-md text-sm md:text-base bg-white">
                  <option value="">Select Type</option>
                  <option value="Upper Wear">Upper Wear</option>
                  <option value="Lower Wear">Lower Wear</option>
                  <option value="Inner Wear">One-Piece</option>
                </select>

                <select value={subType} onChange={(e) => setSubType(e.target.value)}
                  className="border border-gray-300 p-2 rounded-md text-sm md:text-base bg-white">
                  <option value="">Select Sub-Type</option>
                  <option value="T-shirt">T-shirt</option>
                  <option value="Shirt">Shirt</option>
                  <option value="Jacket">Jacket</option>
                  <option value="Tops">Tops</option>
                  <option value="Jeans">Jeans</option>
                  <option value="Trousers">Trousers</option>
                  <option value="Shorts">Shorts</option>
                  <option value="Undergarments">Undergarments</option>
                  <option value="Thermals">Thermals</option>
                  <option value="Shoes">Dress</option>
                  <option value="Sneakers">Suit</option>
                </select>
              </>
            )}

            <button 
            onClick={handleUpload}
            className="bg-[#3913C5] text-white py-2 px-4 rounded-md text-sm md:text-base disabled:opacity-50"
            disabled={uploading}>
            {uploading ? `Uploading... (${uploadProgress}%)` : "Upload Image"}
          </button>


          </div>
        </div>

        <hr className="my-10 border-gray-300" />
        <div className="flex gap-4 mt-5">
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
            className="border border-gray-300 p-2 rounded-md text-sm md:text-base bg-white">
            <option value="">All Categories</option>
            <option value="Model">Model</option>
            <option value="Garment">Garment</option>
          </select>

          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
            className="border border-gray-300 p-2 rounded-md text-sm md:text-base bg-white">
            <option value="">All Types</option>
            <option value="Upper Wear">Upper Wear</option>
            <option value="Lower Wear">Lower Wear</option>
            <option value="Inner Wear">Inner Wear</option>
            <option value="Footwear">Footwear</option>
          </select>

          <select value={filterSubType} onChange={(e) => setFilterSubType(e.target.value)}
            className="border border-gray-300 p-2 rounded-md text-sm md:text-base bg-white">
            <option value="">All Sub-Types</option>
            <option value="T-shirt">T-shirt</option>
            <option value="Shirt">Shirt</option>
            <option value="Jacket">Jacket</option>
            <option value="Tops">Tops</option>
            <option value="Jeans">Jeans</option>
            <option value="Trousers">Trousers</option>
            <option value="Shorts">Shorts</option>
            <option value="Undergarments">Undergarments</option>
            <option value="Thermals">Thermals</option>
            <option value="Shoes">Shoes</option>
            <option value="Sneakers">Sneakers</option>
            <option value="Boots">Boots</option>
          </select>
        </div>

        <hr className="my-10 border-gray-300" />

        <h2 className="text-xl md:text-2xl font-semibold mt-8 md:mt-10">Assets</h2>
        {loadingImages ? (
          <div className="flex justify-center items-center h-48 sm:h-64">
            <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-t-2 border-b-2 border-[#3913C5]"></div>
          </div>
        ) : (
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {currentImages.map((item, index) => (
            <div key={index} className="relative bg-[#F7F5FF] p-3 rounded-lg shadow-md overflow-hidden"
              onMouseEnter={() => setHoveredImage(index)}
              onMouseLeave={() => setHoveredImage(null)}>
              <img src={item.url} alt={item.imageName} className="w-full h-32 md:h-40 object-contain rounded-lg" />
              <p className="text-xs md:text-sm font-semibold mt-2">{item.imageName}</p>
              <p className="text-xs text-gray-500">{item.category}</p>
              {hoveredImage === index && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex justify-center items-center gap-2 md:gap-3">
                  <button className="bg-white p-2 rounded-full hover:bg-gray-200"
                    onClick={() => handlePreview(item.url)}>
                    <Eye className="text-gray-700" size={18} />
                  </button>

                  <button className="bg-red-600 p-2 rounded-full hover:bg-red-700"
                    onClick={() => handleDelete(item.storagePath)}>
                    <Trash2 className="text-white" size={18} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
        )}
        {totalPages > 1 && (
          <div className="flex justify-center mt-8 space-x-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-[#3913C5] text-white rounded-md disabled:opacity-50"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
              <button
                key={number}
                onClick={() => paginate(number)}
                className={`px-4 py-2 rounded-md ${
                  currentPage === number
                    ? "bg-[#3913C5] text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {number}
              </button>
            ))}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-[#3913C5] text-white rounded-md disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
        </div>
      </main>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    {modalImage && (
      <div
        className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50"
        onContextMenu={(e) => e.preventDefault()} // ❌ Disable right-click
      >
        <div className="relative bg-white rounded-lg shadow-lg p-4 max-w-4xl w-full">
          <img
            src={modalImage}
            alt="Preview"
            className="w-full h-auto object-contain max-h-[80vh] rounded"
            draggable="false"
            onContextMenu={(e) => e.preventDefault()}
          />
          <button
            className="absolute top-2 right-2 text-white bg-red-500 hover:bg-red-600 rounded-full px-3 py-1 text-sm"
            onClick={() => setModalImage(null)}
          >
            ✕ Close
          </button>
        </div>
      </div>
    )}
    </div>
    
  );
}

export default MyAssets;